/* eslint-disable max-len */
import { DEFAULT_COUPON_TAB_ITEM } from '@/config';
import { CouponBlackItem, IPeriodCtrlConfig, CouponTabItem } from '@/types/config-type';
import { to } from '@/utils';
import { CustomException, language } from '@tencent/ppd-base-libs';
import { handleFormatRes } from '@tencent/wphk-act-base-cgi';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { getEStampAllConfig } from '../api/index';
import { checkTimeBetween, getMonthPeriod, getServerTime } from '../utils/date-helper';
import { tryDecode } from '../utils/index';

const defaultConfigStore: IPeriodCtrlConfig = {
  commonConfig: {} as any,
  periodConfig: {} as any,
  taskConfig: {} as any,
  couponConfig: {} as any,
};

export const useConfigStore = defineStore({
  id: 'configStore', // 必填

  state: () => ({
    config: defaultConfigStore,
    isInit: false,
  }),

  getters: {}, // 非必要转换别填

  actions: {
    async init() {
      const [err, res] = await to(getEStampAllConfig());
      if (err) {
        // ajax config失败告警
        throw new CustomException(err, 'eStampLoadConfigFail', '新版狂赏印加载配置失败');
      }
      const config = getConfigByPeriod(res.configList);
      // 需要这样写，否则config会失去响应式
      this.config = Object.assign(this.config, formatConfig(config));
      this.isInit = true;
      console.log('前端配置初始化结果,当前配置为：', this.config);
    },
  },
});

/**
 * 根据当前时间从全量配置中获取当前生效配置，如果找不到则告警。
 * @param configArr 配置系统返回的全量配置。zen：可以先看下https://st.moneydata.hk/res/cc/eStampAllConfig/config.json的数据
 * @return object 当前生效的配置
 */
const getConfigByPeriod = (configArr: any[]) => {
  const serverTime = getServerTime();
  const targetConfig = configArr.find((item) => {
    const { startTime, endTime } = getArrayFirstItemAsObj(item.periodConfig);

    if (!startTime || !endTime || !serverTime) {
      throw new CustomException({ startTime, endTime, serverTime }, 'eStampConfigTimeEmpty', '新版狂赏印时间配置异常');
    }
    // 转成Date格式
    const startDate = new Date(dayjs(startTime).format('YYYY/MM/DD HH:mm:ss'));
    const endDate = new Date(dayjs(endTime).format('YYYY/MM/DD HH:mm:ss'));
    // 校验时间，返回当前时间区间的配置
    return checkTimeBetween({ targetTime: serverTime, startDate, endDate });
  });
  if (!targetConfig) {
    throw new CustomException({ serverTime }, 'eStampConfigNotFound', '新版狂赏印配置为空');
  }
  return targetConfig;
};

/** 配置格式化 */
const formatConfig = (config) => {
  const formattedConfigType = formatConfigType(config);
  const {
    taskConfig: { taskItemConfig, taskGroupConfig },
    couponConfig: { originalCoupon, couponItemConfig, recommendCouponGroup, couponBlackListConfig, couponTabConfig },
    periodConfig,
    commonConfig,
  } = formattedConfigType || {};
  // 格式化后的券分类
  const couponTabConfigWithLang = getConfigWithLang({
    originalObj: (couponTabConfig?.tabConfig || []) as any[],
    configKey: ['tabName', 'tabTips'],
  });
  const formatedCouponTabConfig = formatCouponTabConfig(couponTabConfigWithLang);
  const tabSortObj = getCouponTabSort(formatedCouponTabConfig); // 券分类排序对象
  // 货源券配置合并
  const mergedCoupon = mergeCouponConfig({ couponItemConfig, originalCoupon, recommendCouponGroup, tabSortObj });
  const commonConfigWithLang = getConfigWithLang({
    originalObj: commonConfig as object,
    configKey: ['leftImageUrl', 'tipsText', 'text'],
  });
  const couponConfigWithLang = getConfigWithLang({
    originalObj: mergedCoupon as any[],
    configKey: ['couponRange', 'couponName', 'v2couponName'],
  });
  const taskConfigWithLang = getConfigWithLang({
    originalObj: taskItemConfig as any[],
    configKey: ['taskName', 'taskRule'],
  });
  const result = {
    periodConfig,
    commonConfig: formatCommonConfig(commonConfigWithLang),
    couponConfig: {
      couponBlackListConfig: formatCouponBlackListConfig(couponBlackListConfig),
      couponListConfig: couponConfigWithLang,
      couponTabConfig: formatedCouponTabConfig,
    },
    taskConfig: formatTaskConfig({
      taskGroupConfig,
      taskItemConfig: taskConfigWithLang,
    }),
  };
  return result;
};

/**
 * 转config格式：date,int
 * 数字 => int
 * 时间戳 => Date
 */
const formatConfigType = (inputObj: object) => {
  // totalCount, discount 暂时先不用
  // FIXME: originalCouponConfig中startTime和endTime有问题
  const needFen2YuanKey = ['requiredNum', 'discount'];
  const needParseIntKey = [
    'stampCntPerTime',
    'maxTimePerDay',
    'sort',
    'monthStampLimit',
    'dayStampLimit',
    'expireDay',
    'perCount',
    'totalCount',
    'validDay',
    'perCount',
    'tabSort',
    'taskSendLimit',

    'validExpireType',
    'couponType',
  ];
  const needParseDateKey = [
    'tipsStartTime',
    'tipsEndTime',
    'startTime',
    'endTime',
    'taskStartTime',
    'taskEndTime',
    'tagEndTime',
  ];
  return handleFormatRes({ result: inputObj, needParseDateKey, needParseIntKey, needFen2YuanKey });
};

/**
 * 合并券配置
 * zen：因为配置还保留mojo的格式，不太方便；合并后使用更合理。
 * 根据couponId合并
 */
const mergeCouponConfig = ({ couponItemConfig, originalCoupon, recommendCouponGroup, tabSortObj }) => {
  const couponArray = getArrayFirstItemAsObj(originalCoupon).couponArray || [];
  const mergedCouponConfig = (couponItemConfig || []).map((couponItem) => {
    const targetCoupon = couponArray.find((originalItem) => originalItem.couponId === couponItem.couponId);
    // 合并推荐券分类配置
    const recommendCouponConfig = (recommendCouponGroup || []).find(
      (groupItem) => groupItem.couponId === couponItem.couponId,
    );
    const { recommendGroup = '' } = recommendCouponConfig || {};
    const formattedFields = {
      ...formatCouponValidPeriod(targetCoupon),
      ...decodeCouponFields(targetCoupon),
      isTimeLimitCoupon: couponItem.isTimeLimitCoupon === '是',
      // 券分类过滤&排序
      tabKey: couponItem.tabKey
        .filter((tab: string) => !!tabSortObj[tab])
        .sort((a: string, b: string) => (tabSortObj[a] - tabSortObj[b])),
    };
    return { ...targetCoupon, ...couponItem, recommendGroup, ...formattedFields };
  });
  return mergedCouponConfig;
};

/** 批量根据语言环境处理对象/数组 */
const getConfigWithLang = ({ configKey, originalObj }: { configKey: string[]; originalObj: object | any[] }) => {
  const lang = language.getLanguage();
  const resObj = {};
  if (Array.isArray(originalObj)) {
    return originalObj.map((item) => {
      if (Array.isArray(item) || Object.prototype.toString.call(item) === '[object Object]') {
        return getConfigWithLang({ configKey, originalObj: item });
      }
      return item;
    });
  }
  for (const [key, value] of Object.entries(originalObj)) {
    if (configKey.includes(key)) {
      resObj[key] = lang === 'en' ? originalObj[`${key}En`] : value;
    } else if (Array.isArray(value)) {
      resObj[key] = getConfigWithLang({ configKey, originalObj: value });
    } else if (Object.prototype.toString.call(value) === '[object Object]') {
      resObj[key] = getConfigWithLang({ configKey, originalObj: value });
    } else if (!configKey.includes(key.replace(/(.*)En/, '$1'))) {
      resObj[key] = value;
    }
  }
  return resObj;
};

/** 格式化任务配置（除了通用format以外的特殊format） */
const formatTaskConfig = ({ taskGroupConfig, taskItemConfig }) => {
  // 处理任务组配置
  const formattedTaskGroupConfig = taskGroupConfig.map((item) => {
    const { blackListActId, blackListType } = getArrayFirstItemAsObj(item.blackListConfig) || {};
    let formattedItem = { ...item };
    // 如果黑白名单配置不完整，那么blackListConfig为 undefined
    if (!blackListActId || !blackListType) {
      formattedItem = {
        ...formattedItem,
        blackListConfig: undefined,
      };
    } else {
      formattedItem = {
        ...formattedItem,
        blackListConfig: {
          blackListActId: tryDecode(blackListActId),
          blackListType,
        },
      };
    }
    return formattedItem;
  });

  // 处理单个任务配置
  const formattedTaskItemConfig = taskItemConfig.map((item) => {
    const { taskStartTime, taskEndTime, btnMpConfig, maxTimePerDay, autoExecConfig } = item;
    let formattedItem = { ...item, taskNameId: String(item.taskNameId).trim() };

    // 处理没有配置起始结束时间，则取本月第一天或最后一天
    if (!taskStartTime || !taskEndTime) {
      const { firstDate, lastDate } = getMonthPeriod();
      formattedItem = {
        ...formattedItem,
        taskStartTime: taskStartTime || firstDate,
        taskEndTime: taskEndTime || lastDate,
      };
    }

    // 处理小程序跳转配置: 数组转对象; 如果没有填btnMpConfig,则该值为undefined
    const { username, mpPath } = getArrayFirstItemAsObj(btnMpConfig) || {};
    if (!username || !mpPath) {
      formattedItem = { ...formattedItem, btnMpConfig: undefined };
    } else {
      formattedItem = { ...formattedItem, btnMpConfig: { username, path: mpPath } };
    }

    // 处理maxTimePerDay可能为NaN的情况
    if (!maxTimePerDay) {
      formattedItem = { ...formattedItem, maxTimePerDay: 0 };
    }

    // 處理自動執行任務的配置; 如果沒有填autoExecConfig，則該值為undefined
    const { autoExecTaskId, autoExecQualId, execTaskType } = getArrayFirstItemAsObj(autoExecConfig) || {};
    if (!autoExecConfig || !autoExecQualId || !execTaskType) {
      formattedItem = { ...formattedItem, autoExecConfig: undefined };
    } else {
      formattedItem = { ...formattedItem, autoExecConfig: { autoExecTaskId, autoExecQualId, execTaskType } };
    }

    return formattedItem;
  });
  return { taskGroupConfig: formattedTaskGroupConfig, taskItemConfig: formattedTaskItemConfig };
};

/**
 * 配置里：通用配置结构都是“[{xxx}]”这个样式，将{xxx}提取出来，不需要[]包裹了，
 * @param commonConfig
 */
const formatCommonConfig = (commonConfig) => {
  const formattedCommonConfig = {};
  // {actConfig,stampConfig,noticeConfig,blockDialogConfig, tncModuleConfig, subscribeModuleConfig}
  for (const [key, value] of Object.entries(commonConfig)) {
    formattedCommonConfig[key] = getArrayFirstItemAsObj(value as any[]);
  }
  return formattedCommonConfig;
};
/**
 * 格式化券黑白名单配置
 * 1. 过滤：同时配置了黑白名单活动 id 和 类型
 * 2. 格式化：黑白名单id格式化
 */
function formatCouponBlackListConfig(couponBlackListConfig: CouponBlackItem[]) {
  return (couponBlackListConfig || [])
    .filter((item: CouponBlackItem) => item.blackListActId && item.blackListType && item.couponId)
    .map((item: CouponBlackItem) => ({
      blackListActId: tryDecode(item.blackListActId),
      blackListType: item.blackListType,
      couponId: item.couponId,
    }));
}
/**
 * 格式化券分类 tab 配置，设置默认值
 * @param couponTabConfig
 * @returns
 */
function formatCouponTabConfig(couponTabConfig: CouponTabItem[] = []) {
  return couponTabConfig
    .map((tabConf) => ({
      ...tabConf,
      tabColor: tabConf.tabColor || DEFAULT_COUPON_TAB_ITEM.tabColor,
      tabActiveColor: tabConf.tabActiveColor || DEFAULT_COUPON_TAB_ITEM.tabActiveColor,
      tabSort: tabConf.tabSort || DEFAULT_COUPON_TAB_ITEM.tabSort,
    }))
    .sort((a, b) => a.tabSort - b.tabSort);
}
/**
 * 格式化源券數據生效時間
 * 10位時間戳轉Date
 */
const formatCouponValidPeriod = (coupon) => {
  const { validTime, expireTime } = coupon || {};
  const formatValidTime = new Date(validTime * 1000);
  const formatExpireTime = new Date(expireTime * 1000);
  return { validTime: formatValidTime, expireTime: formatExpireTime };
};

/** decode优惠券的字段 */
const decodeCouponFields = (coupon) => {
  const { couponCond, customizeJumpName, customizeJumpUrl, couponName, couponRange } = coupon || {};
  return {
    // 展示券需要decode
    couponName: tryDecode(couponName),
    couponCond: tryDecode(couponCond),
    couponRange: tryDecode(couponRange),
    customizeJumpName: tryDecode(customizeJumpName),
    customizeJumpUrl: tryDecode(customizeJumpUrl),
  };
};

/** 兼容配置系统： 把数组的第一项作为对象返回 */
const getArrayFirstItemAsObj = (arr: any[]) => arr?.[0] || {};

// 获取券排序对象
function getCouponTabSort(couponTabConfig: CouponTabItem[] = []) {
  const tabSortObj = {};
  for (const tab of couponTabConfig) {
    tabSortObj[tab.tabKey] = tab.tabSort;
  }
  return tabSortObj;
}
